import { ApolloClient, ApolloLink, concat, HttpLink } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  DEVICE_TYPE,
  deviceId,
  DeviceType,
  EMULATED_USER_ID,
  HEADER_APP_TYPE_ID,
  HEADER_DEVICE_ID,
  HEADER_USER_ID,
  logout,
  USER_PROJECT_ID,
} from '@bechtel/client-common';
import QueueLink from 'apollo-link-queue';
import { persistCache, SessionStorageWrapper } from 'apollo3-cache-persist';
import fetch from 'cross-fetch';
import { sha256 } from 'crypto-hash';
import { inflate } from 'graphql-deduplicator';
import { createClient } from 'graphql-ws';
import { get } from 'lodash';
import { API_WS, GATEWAY_SERVICE_URL } from './env-config';

const gatewayServiceUrl = `${GATEWAY_SERVICE_URL}/graphql`;
const wsURI = API_WS;
// // https://github.com/helfer/apollo-link-queue
const queueLink = new QueueLink();

window.addEventListener('offline', () => queueLink.close());
window.addEventListener('online', () => queueLink.open());

// https://www.apollographql.com/docs/link/links/error/
// TODO: if backend integrates an apollo level UNAUTHORIZED enum response
// we need to integrate with that along with the network error
const logoutLink = onError(({ networkError }) => {
  const statusCode = get(networkError, 'statusCode');

  if (statusCode && statusCode === 401) {
    // TODO: refresh token logic implementation
    // TODO: look at purging apollo cache potentially
    logout();
  }
});

// NOTE: this accounts for logging out in one tab but not another
window.addEventListener('storage', (e) => {
  if (e.key === 'authenticated' && !!e.newValue) {
    logout();
  }
});

// https://www.apollographql.com/docs/link/links/retry/
const retryLink = new RetryLink({
  attempts: {
    retryIf: (error) => error && error.statusCode !== 401,
  },
});

// https://www.apollographql.com/docs/link/links/error/
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) => {
      console.log('[GraphQL error]:');
      console.log(`Message: ${message}`);
      console.log(`Location: ${JSON.stringify(locations, null, 2)}`);
      console.log(`Path: ${JSON.stringify(path, null, 2)}`);
    });
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }
});

function cursorLimitPagination(
  keyArgs: string[] = [],
  isRowNumberBased = false,
  getIdentifiter: ((readField: any, item: any) => any) | null = null
) {
  return {
    keyArgs,
    read: (existing) => {
      if (existing) {
        const cursor = isRowNumberBased
          ? {
              rowNumber: existing.rowNumber,
            }
          : {
              cursor: existing.cursor,
            };

        return {
          ...cursor,
          items: Object.values(existing.items),
          total: existing.total,
        };
      }
    },
    merge: (existing, incoming, { readField }) => {
      const items = existing ? { ...existing.items } : {};
      incoming.items?.forEach((item) => {
        if (getIdentifiter) {
          items[getIdentifiter(readField, item)] = item;
        } else items[readField('id', item)] = item;
      });
      const cursor = isRowNumberBased
        ? {
            rowNumber: incoming.rowNumber,
          }
        : {
            cursor: incoming.cursor,
          };

      return {
        ...cursor,
        items,
        total: incoming.total,
      };
    },
  };
}

const cache: any = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        workPackagesByIds(existingData, { args, toReference }) {
          return (
            existingData ||
            toReference({
              __typename: 'WorkPackage',
              ids: args?.ids,
            })
          );
        },
        workPackages: cursorLimitPagination(['input']),
        commodities: cursorLimitPagination(['input'], false, (readField, item) => {
          if (readField('id', readField('materialCommodity', item)))
            return readField('id', readField('materialCommodity', item));
          else return readField('id', readField('weld', item));
        }),
        blocks: cursorLimitPagination(['input']),
        users: cursorLimitPagination(['input']),
        reports: cursorLimitPagination(['input']),
        weeklyWorkList: cursorLimitPagination(['input'], true),
        crewChangeRequests: cursorLimitPagination(['input'], true),
        eventNotifications: cursorLimitPagination(['input']),
        costCodes: cursorLimitPagination(['input']),
        weeklyWorkListBulk: cursorLimitPagination(['input'], true),
      },
    },
    MaterialCommodity: { fields: { milestonesActivityHistory: cursorLimitPagination(['input']) } },
    Weld: { fields: { milestonesActivityHistory: cursorLimitPagination(['input']) } },
  },
});

// https://github.com/apollographql/apollo-cache-persist
(async () =>
  await persistCache({
    cache,
    storage: new SessionStorageWrapper(window.sessionStorage),
  }))();

//https://www.apollographql.com/docs/react/api/link/apollo-link-http/#httplink-constructor-options
const httpLink = new HttpLink({
  uri: `${gatewayServiceUrl}?deduplicate=1`,
  credentials: 'include',
  fetch,
});

// @ts-ignore: https://github.com/gajus/graphql-deduplicator
const inflateLink = new ApolloLink((operation, forward) => forward(operation).map((response) => inflate(response)));

// const webSocketLink = new WebSocketLink({
//   uri: `${wsURI}`,
//   options: {
//     reconnect: true,
//     timeout: 30000,
//     reconnectionAttempts: 3,
//   },
// });

const webSocketLink = new GraphQLWsLink(
  createClient({
    url: `${wsURI}`,
    keepAlive: 10_000,
    connectionParams: () => ({
      [HEADER_DEVICE_ID]: deviceId,
      [USER_PROJECT_ID]: window.sessionStorage.getItem(USER_PROJECT_ID) || '',
      [HEADER_APP_TYPE_ID]: 'PF',
    }),
  })
);

//TODO: Backend does not deflate the websockets response so removed InflateLink from websockets
const nonTerminatingWsLinks: any = [logoutLink, retryLink, errorLink, queueLink];
const nonTerminatingLinks = [...nonTerminatingWsLinks, inflateLink];
const linkWS = ApolloLink.from([...nonTerminatingWsLinks, webSocketLink]);

const linkHTTP = ApolloLink.from([...nonTerminatingLinks, httpLink]);

const link = createPersistedQueryLink({ sha256 }).concat(
  ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    linkWS,
    linkHTTP
  )
);

const appMiddleware = new ApolloLink((operation, forward) => {
  const emulatedUserId = window.sessionStorage.getItem(EMULATED_USER_ID);
  const userProjectId = window.sessionStorage.getItem(USER_PROJECT_ID);
  const userId = window.sessionStorage.getItem(HEADER_USER_ID);

  const appHeaders = {};
  if (emulatedUserId && emulatedUserId != '') appHeaders[EMULATED_USER_ID] = emulatedUserId;
  if (userProjectId && userProjectId != '') appHeaders[USER_PROJECT_ID] = userProjectId;

  appHeaders[DEVICE_TYPE] = DeviceType.WEB;
  appHeaders[HEADER_USER_ID] = userId;
  appHeaders[HEADER_DEVICE_ID] = deviceId;

  const sendHeaders = Object.keys(appHeaders).length !== 0;

  operation.setContext(({ headers = {} }) => {
    const allHeaders = sendHeaders
      ? {
          headers: {
            ...headers,
            ...appHeaders,
          },
        }
      : {
          headers: {
            ...headers,
          },
        };
    return allHeaders;
  });
  return forward(operation);
});

export const client = new ApolloClient({
  cache,
  link: concat(appMiddleware, link),
  assumeImmutableResults: true,
});
